import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Generate initials from a name or title
 * - Single word: First letter only (e.g., "Product" → "P")
 * - Multiple words: First letter of first two words (e.g., "User Experience" → "UX")
 */
export function getInitials(name: string): string {
  if (!name || name.trim().length === 0) return '';
  
  const words = name.trim().split(' ').filter(word => word.length > 0);
  
  if (words.length === 0) return '';
  if (words.length === 1) return words[0].charAt(0).toUpperCase();
  
  // Take first letter of first two words
  return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
}

/**
 * Format a date string into a friendly "time ago" format
 * @param date - ISO date string (e.g., "2025-08-10T17:16:30.000000Z")
 * @returns Formatted string like "30 days ago", "2 hours ago", etc.
 */
export function formatTimeAgo(date: string | Date): string {
  const now = new Date();
  const targetDate = new Date(date);
  
  const totalSeconds = Math.abs(Math.floor((now.getTime() - targetDate.getTime()) / 1000));
  
  if (totalSeconds < 60) {
    return 'just now';
  }
  
  const minutes = Math.floor(totalSeconds / 60);
  if (minutes < 60) {
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  }
  
  const hours = Math.floor(totalSeconds / 3600);
  if (hours < 24) {
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  }
  
  const days = Math.floor(totalSeconds / 86400);
  if (days < 7) {
    return `${days} day${days > 1 ? 's' : ''} ago`;
  }
  
  if (days < 30) {
    const weeks = Math.floor(days / 7);
    return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  }
  
  if (days < 365) {
    const months = Math.floor(days / 30);
    return `${months} month${months > 1 ? 's' : ''} ago`;
  }
  
  const years = Math.floor(days / 365);
  return `${years} year${years > 1 ? 's' : ''} ago`;
}

 